import React, { memo, useContext, useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext } from 'src/contexts/modal-context';
import { FormBuilder } from '@components/form';
import { userSelector } from '@redux/slices/users';
import { fetchApprovalGroups } from '@redux/slices/approval-groups';
import { fetchLines } from '@redux/slices/divisions';
import { translate } from '@components/i18n';
import { TransferClient } from '@models/transfer-client';
import { addTransferClients, deleteTransferClient, updateTransferClients } from '@redux/slices/transfer-clients';
import { authSelector } from '@redux/slices/auth';
import { distributorSelector, fetchDistributors } from '@redux/slices/distributor';
import { editableFields } from '../constants';

interface EditModalProps {
    data?: TransferClient.Response;
    reload?: () => void
}

export const EditModal: React.FC<EditModalProps> = memo(({ data, reload }) => {
    const auth = useSelector(authSelector)
    const users = useSelector(userSelector)
    const distributors = useSelector(distributorSelector);

    const dispatch = useDispatch();
    const [form] = Form.useForm<TransferClient.Response>();
    const [editState, setEditState] = useState<TransferClient.Response>(data);
    const modalContext = useContext(ModalContext);

    const canDelete = !!data && !auth.isPromoter

    const onOk = (values): void => {
        if (!data) addNew(values);
        else handleUpdate(values);
    };

    const handleUpdate = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.updatingTransferClient'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            updateTransferClients({
                data: {
                    id: data.id,
                    ...values,
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                    if (reload) {
                        reload()
                    }
                },
            }),
        );
    };

    const addNew = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.registeringTransferClient'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        if (!auth.isAdmin) {
            values.idPromotor = auth.currentUser.id
        }

        dispatch(
            addTransferClients({
                data: {
                    ...values,
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                    if (reload) {
                        reload()
                    }
                },
            }),
        );
    };

    useEffect(() => {
        dispatch(fetchDistributors());
        dispatch(fetchApprovalGroups());
        dispatch(fetchLines());
    }, []);

    useEffect(() => {
        if (data) {
            setEditState(data);
            form.setFieldsValue(data);
        } else {
            form.resetFields();
        }
    }, [data]);

    const onChangeValues = (
        changedValues: Partial<TransferClient.Response>,
        allValues: TransferClient.Response,
    ): void => {
        setEditState({
            ...allValues,
            ...changedValues,
        });
    };

    const handleDelete = (): void => {
        dispatch(deleteTransferClient(data.id));
        modalContext.handleClose();
    };

    return (
        <Row align="middle">
            <Col span={1} />
            <Col span={22}>
                <FormBuilder
                    onValuesChange={onChangeValues}
                    layout="vertical"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{ ...editState }}
                    onFinish={onOk}
                    form={form}
                    fields={editableFields({
                        promoters: users?.availableUsers?.map(c => {
                            if (c.id === auth?.currentUser?.id && auth.isAdmin) {
                                return ({
                                    id: c.id,
                                    name: `${c.jdeCode} - (Eu) ${c.name}`,
                                })
                            }

                            return ({
                                id: c.id,
                                name: `${c.jdeCode} - ${c.name}`,
                            })
                        }) || [],
                        distributors: distributors?.available?.length > 0 ? distributors?.available?.map(item => ({
                            id: item.codigo,
                            name: `${item.codigo} - ${item.nome}`,
                        })) : []
                    })}
                    withDelete={canDelete}
                    onDelete={handleDelete}
                />
            </Col>
        </Row>
    );
});
